/**
 * Button component with variants, loading state, and fun effects
 */
import { forwardRef, ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { Spinner } from './Spinner';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'gradient';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  glow?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-500 active:bg-primary-700 disabled:bg-primary-800 disabled:text-primary-400',
  secondary:
    'bg-dark-700 text-dark-100 hover:bg-dark-600 active:bg-dark-800 disabled:bg-dark-800 disabled:text-dark-500',
  outline:
    'border border-dark-600 bg-transparent text-dark-200 hover:bg-dark-800 hover:border-dark-500 active:bg-dark-700 disabled:bg-transparent disabled:text-dark-600',
  ghost:
    'bg-transparent text-dark-300 hover:bg-dark-800 hover:text-dark-100 active:bg-dark-700 disabled:text-dark-600',
  danger:
    'bg-rose-600 text-white hover:bg-rose-500 active:bg-rose-700 disabled:bg-rose-800 disabled:text-rose-400',
  gradient:
    'bg-gradient-to-r from-primary-600 to-accent-600 text-white hover:from-primary-500 hover:to-accent-500 active:from-primary-700 active:to-accent-700 disabled:from-primary-800 disabled:to-accent-800 disabled:text-white/50',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

const glowClasses: Record<ButtonVariant, string> = {
  primary: 'hover:shadow-glow-blue',
  secondary: '',
  outline: 'hover:shadow-glow-blue',
  ghost: '',
  danger: 'hover:shadow-glow-red',
  gradient: 'hover:shadow-glow-purple',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      glow = false,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
          'transition-all duration-200 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900',
          'disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          glow && glowClasses[variant],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Spinner size="sm" className="text-current" />
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
